import React, { useState, useEffect } from "react";
import { debounce } from "lodash";
import {
  TextField,
  FormControlLabel,
  Switch,
  InputAdornment,
  Alert,
  Box,
  CircularProgress,
  Typography,
} from "@mui/material";
import { Modal } from "ui-components/modal";
import { observer } from "mobx-react-lite";
import { LoadingButton } from "@mui/lab";
import { $fastOrderLinks } from "../../../store/fastOrderLinks";
import { $modals } from "../../../store/modals";
import { $snackbar } from "../../../store/snackbar";
import { $products } from "../../../store/products/products";

const EditDiscountModal = observer(() => {
  // Все хуки должны быть объявлены до любых условных возвратов
  const [productId, setProductId] = useState("");
  const [originalPrice, setOriginalPrice] = useState("");
  const [discountPrice, setDiscountPrice] = useState("");
  const [discountPercent, setDiscountPercent] = useState("");
  const [beginAt, setBeginAt] = useState("");
  const [endAt, setEndAt] = useState("");
  const [maxCount, setMaxCount] = useState(1);
  const [salesLimit, setSalesLimit] = useState(1);
  const [hideOldPrice, setHideOldPrice] = useState(false);
  const [useRetailPrice, setUseRetailPrice] = useState(false);
  const [errors, setErrors] = useState({});
  const [btnLoading, setBtnLoading] = useState(false);
  const [productLoading, setProductLoading] = useState(false);
  const [productNotFound, setProductNotFound] = useState(false);
  const [currentProduct, setCurrentProduct] = useState(null);

  const discount = $modals.editFastOrderDiscount.data;

  useEffect(() => {
    if (discount) {
      setProductId(discount.product_id?.toString() || "");
      setOriginalPrice(discount.original_price?.toString() || "");
      setDiscountPrice(discount.discount_price?.toString() || "");
      setBeginAt(discount.begin_at ? formatDateForInput(discount.begin_at) : "");
      setEndAt(discount.end_at ? formatDateForInput(discount.end_at) : "");
      setMaxCount(discount.max_count ?? 1);
      setSalesLimit(discount.sales_limit ?? 1);
      setHideOldPrice(Boolean(discount.hide_old_price));
      setUseRetailPrice(Boolean(discount.use_retail_price));
      
      // Рассчитываем процент скидки при загрузке
      if (discount.original_price && discount.discount_price) {
        const percent = Math.round(((discount.original_price - discount.discount_price) / discount.original_price) * 100);
        setDiscountPercent(percent > 0 ? percent.toString() : "");
      }
      
      // Загружаем информацию о товаре для отображения
      if (discount.product_id) {
        $products.fetchProduct(discount.product_id)
          .then(product => {
            if (product) {
              setCurrentProduct(product);
              setProductNotFound(false);
            }
          })
          .catch(() => {
            setProductNotFound(true);
            setCurrentProduct(null);
          });
      }
    }
  }, [discount]);

  const formatDateForInput = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toISOString().slice(0, 16);
  };

  // Функция для загрузки товара
  const fetchProductById = async (id) => {
    if (id && !isNaN(id)) {
      setProductLoading(true);
      try {
        const product = await $products.fetchProduct(id);
        if (product && product.price_per_piece) {
          setOriginalPrice(product.price_per_piece);
          setCurrentProduct(product);
          setProductNotFound(false);
          // Пересчитываем процент при изменении оригинальной цены
          if (discountPrice && !isNaN(discountPrice)) {
            const percent = Math.round(((product.price_per_piece - parseFloat(discountPrice)) / product.price_per_piece) * 100);
            setDiscountPercent(percent > 0 ? percent.toString() : "");
          }
        } else {
          setOriginalPrice("");
          setCurrentProduct(null);
          setProductNotFound(true);
        }
      } catch (error) {
        console.error('Ошибка при загрузке товара:', error);
        setOriginalPrice("");
        setCurrentProduct(null);
        setProductNotFound(true);
      } finally {
        setProductLoading(false);
      }
    } else {
      setOriginalPrice("");
      setCurrentProduct(null);
      $products.clearCurrentProduct();
    }
  };

  // Debounced функция для загрузки товара
  const debouncedFetchProduct = debounce(fetchProductById, 500);

  // Обработчик изменения ID товара
  const handleProductIdChange = (e) => {
    const id = e.target.value;
    setProductId(id);
    setProductNotFound(false);
    setCurrentProduct(null);
    
    // Отменяем предыдущий запрос и запускаем новый с задержкой
    debouncedFetchProduct(id);
  };

  // Расчет процента при изменении цены со скидкой
  const handleDiscountPriceChange = (e) => {
    const price = e.target.value;
    setDiscountPrice(price);
    
    if (price && originalPrice && !isNaN(price) && !isNaN(originalPrice)) {
      const percent = Math.round(((parseFloat(originalPrice) - parseFloat(price)) / parseFloat(originalPrice)) * 100);
      setDiscountPercent(percent > 0 ? percent.toString() : "");
    } else {
      setDiscountPercent("");
    }
  };

  // Расчет цены при изменении процента скидки
  const handleDiscountPercentChange = (e) => {
    const percent = e.target.value;
    setDiscountPercent(percent);
    
    if (percent && originalPrice && !isNaN(percent) && !isNaN(originalPrice) && parseFloat(percent) >= 0 && parseFloat(percent) <= 100) {
      const price = parseFloat(originalPrice) * (1 - parseFloat(percent) / 100);
      setDiscountPrice(Math.round(price).toString());
    } else if (!percent) {
      setDiscountPrice("");
    }
  };

  const handleMaxCount = (e) => {
    setMaxCount(e.target.value);
  };

  const onSubmit = (e) => {
    e.preventDefault();
    setBtnLoading(true);
    setErrors({});

    const data = {
      product_id: parseInt(productId),
      original_price: parseFloat(originalPrice),
      discount_price: Math.round(parseFloat(discountPrice)), // Округляем без копеек
      begin_at: beginAt || null,
      end_at: endAt || null,
      max_count: maxCount,
      sales_limit: parseInt(salesLimit, 10),
      min_count: discount.min_count ?? 1,
      hide_old_price: hideOldPrice,
      use_retail_price: useRetailPrice,
    };

    $fastOrderLinks
      .updateDiscount(discount.id, data)
      .then(() => {
        $snackbar.show("Скидка успешно обновлена", "success");
        $modals.editFastOrderDiscount.hide();
      })
      .catch((err) => {
        if (err.response?.data && err.response.status === 422) {
          setErrors(err.response.data.errors);
        } else {
          $snackbar.show("Ошибка при обновлении скидки", "error");
        }
      })
      .finally(() => setBtnLoading(false));
  };

  // Условная проверка видимости после всех хуков
  if (!$modals.editFastOrderDiscount.visible || !discount) {
    return null;
  }

  return (
    <Modal title="Редактирование скидки">
      <form onSubmit={onSubmit}>
        <TextField
          error={!!errors.product_id}
          helperText={errors.product_id}
          required
          value={productId}
          onChange={handleProductIdChange}
          fullWidth
          sx={{ mb: 3 }}
          label="ID товара"
          variant="outlined"
          type="number"
          inputProps={{ min: 1 }}
          InputProps={{
            endAdornment: productLoading && (
              <InputAdornment position="end">
                <CircularProgress size={20} />
              </InputAdornment>
            ),
          }}
        />

        {productNotFound && productId && (
          <Alert severity="error" sx={{ mb: 3 }}>
            Товар с ID {productId} не найден
          </Alert>
        )}

        {currentProduct && (
          <Alert 
            severity="info" 
            sx={{ mb: 3 }}
            icon={false}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              {currentProduct.images && currentProduct.images.length > 0 && (
                <Box
                  component="img"
                  src={currentProduct.images[0].url}
                  alt={currentProduct.name}
                  sx={{
                    width: 50,
                    height: 50,
                    objectFit: 'cover',
                    borderRadius: 1,
                  }}
                />
              )}
              <Box>
                <Typography variant="body2" fontWeight="bold">
                  {currentProduct.name}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Цена: {currentProduct.price_per_piece} ₽
                </Typography>
              </Box>
            </Box>
          </Alert>
        )}

        <TextField
          error={!!errors.original_price}
          helperText={errors.original_price}
          required
          value={originalPrice}
          fullWidth
          sx={{ mb: 3 }}
          label="Цена в ТБП"
          variant="outlined"
          type="number"
          InputProps={{
            readOnly: true,
            endAdornment: <InputAdornment position="end">₽</InputAdornment>,
          }}
          disabled={!originalPrice}
        />

        <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
          <TextField
            error={!!errors.discount_price}
            helperText={errors.discount_price}
            required
            value={discountPrice}
            onChange={handleDiscountPriceChange}
            fullWidth
            label="Цена со скидкой"
            variant="outlined"
            type="number"
            inputProps={{ min: 0, step: 0.01 }}
            InputProps={{
              endAdornment: <InputAdornment position="end">₽</InputAdornment>,
            }}
          />
          
          <TextField
            value={discountPercent}
            onChange={handleDiscountPercentChange}
            fullWidth
            label="% скидки"
            variant="outlined"
            type="number"
            inputProps={{ min: 0, max: 100 }}
            InputProps={{
              endAdornment: <InputAdornment position="end">%</InputAdornment>,
            }}
          />
        </Box>

        <TextField
          error={!!errors.begin_at}
          helperText={errors.begin_at}
          required
          value={beginAt}
          onChange={(e) => setBeginAt(e.target.value)}
          fullWidth
          sx={{ mb: 3 }}
          label="Дата начала"
          variant="outlined"
          type="datetime-local"
          InputLabelProps={{ shrink: true }}
        />

        <TextField
          error={!!errors.end_at}
          helperText={errors.end_at}
          required
          value={endAt}
          onChange={(e) => setEndAt(e.target.value)}
          fullWidth
          sx={{ mb: 3 }}
          label="Дата окончания"
          variant="outlined"
          type="datetime-local"
          InputLabelProps={{ shrink: true }}
        />

        <TextField
          error={!!errors.max_count}
          helperText={errors.max_count}
          value={maxCount}
          onChange={handleMaxCount}
          fullWidth
          sx={{ mb: 3 }}
          label="Доступно к покупке"
          variant="outlined"
          type="number"
          inputProps={{ min: 1, max: 127, step: 1 }}
          InputProps={{
            endAdornment: <InputAdornment position="end">шт</InputAdornment>,
          }}
        />

        <TextField
          error={!!errors.sales_limit}
          helperText={errors.sales_limit}
          required
          value={salesLimit}
          onChange={(e) => setSalesLimit(e.target.value)}
          fullWidth
          sx={{ mb: 3 }}
          label="Лимит продаж по ссылке"
          variant="outlined"
          type="number"
          inputProps={{ min: 1, step: 1 }}
          InputProps={{
            endAdornment: <InputAdornment position="end">шт</InputAdornment>,
          }}
        />

        <FormControlLabel
          sx={{ mb: 1, display: "block" }}
          control={(
            <Switch
              checked={hideOldPrice}
              onChange={(e) => setHideOldPrice(e.target.checked)}
            />
          )}
          label="Скрывать старую цену"
        />

        <FormControlLabel
          sx={{ mb: 3, display: "block" }}
          control={(
            <Switch
              checked={useRetailPrice}
              onChange={(e) => setUseRetailPrice(e.target.checked)}
            />
          )}
          label="Использовать розничную цену (вместо зачеркнутой)"
        />

        <Alert severity="info" sx={{ mb: 3 }}>
          Цена со скидкой будет автоматически округлена до рублей (без копеек)
        </Alert>

        <LoadingButton
          sx={{ float: "right" }}
          type="submit"
          loading={btnLoading}
          variant="contained"
          disabled={productNotFound || !originalPrice}
        >
          Сохранить
        </LoadingButton>
      </form>
    </Modal>
  );
});

export default EditDiscountModal;
